/**
 * Shadow from the sprite crop (2026-09-18 owner report: five Shadow Pokémon
 * scanned, no flames on the HUD). GO prints no "Shadow" text on the Pokémon
 * page; the live signals are the page's own STORM SKY (every Shadow page
 * gets it, a mid-bright blue-violet backdrop) and the violet flame aura
 * around the model. Measured on five device frames (Tyrunt and Bellsprout
 * Shadows; Skeledirge, Chesnaught and Impidimp plain):
 *
 *   - the storm sky is hue 240-255 at 0.4-0.5 value and covers 70-80% of the
 *     crop's flanks; a plain page at night is near-black there (value below
 *     0.2) and a day page is cyan (hue 200-215), so neither counts;
 *   - the flames are hue 260-290 and sit AROUND the model, 4-15% of the crop;
 *     Skeledirge's maroon sky reaches hue 290-300 but has no storm sky.
 *
 * Both are required. A purple species (Gengar, Rattata) on a plain page fails
 * the sky test; a plain page on the storm sky does not exist.
 *
 * The PSYCHIC habitat (device pass, same day: Hatenna, Oricorio) is the one
 * plain backdrop that carries both hues. It is told apart by brightness and
 * extent: its blue-violet averages 0.66-0.74 value against the storm sky's
 * 0.44-0.48, and its violet swirl covers 25-44% of the crop against the
 * flames' 7-18%. Two caps, both with room on either side.
 */
export interface ShadowAuraVerdict {
  shadow: boolean;
  /** Share of the crop that is the storm sky / the violet flames, 0-1. */
  skyFrac: number;
  violetFrac: number;
  /** Mean value (brightness, 0-1) of the sky pixels; 0 when there are none. */
  skyValue: number;
}

const SKY_HUE_MIN = 232;
const SKY_HUE_MAX = 258;
const SKY_MIN_SAT = 0.3;
const SKY_MIN_VAL = 0.2;
const SKY_MAX_VAL = 0.9;
const FLAME_HUE_MIN = 258;
const FLAME_HUE_MAX = 296;
const FLAME_MIN_SAT = 0.45;
const FLAME_MIN_VAL = 0.3;
const FLAME_MAX_VAL = 0.97;
/** The storm sky must cover this much of the crop. */
export const SHADOW_MIN_SKY_FRAC = 0.25;
/** The flames must cover this much of the crop. */
export const SHADOW_MIN_VIOLET_FRAC = 0.03;
/** More violet than this is a habitat backdrop, not flames around a model. */
export const SHADOW_MAX_VIOLET_FRAC = 0.24;
/** The storm sky is dim; a blue-violet brighter than this on average is a habitat. */
export const SHADOW_MAX_SKY_VALUE = 0.58;

function hsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  const d = mx - mn;
  let h = 0;
  if (d > 0) {
    if (mx === r) h = ((g - b) / d) % 6;
    else if (mx === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: mx > 0 ? d / mx : 0, v: mx / 255 };
}

export function classifyShadowAura(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): ShadowAuraVerdict {
  const n = width * height;
  if (width < 20 || height < 20) return { shadow: false, skyFrac: 0, violetFrac: 0, skyValue: 0 };
  let sky = 0;
  let skyValueSum = 0;
  let violet = 0;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const { h, s, v } = hsv(data[o], data[o + 1], data[o + 2]);
    if (h >= SKY_HUE_MIN && h < SKY_HUE_MAX && s >= SKY_MIN_SAT && v >= SKY_MIN_VAL && v <= SKY_MAX_VAL) {
      sky += 1;
      skyValueSum += v;
    } else if (h >= FLAME_HUE_MIN && h <= FLAME_HUE_MAX && s >= FLAME_MIN_SAT && v >= FLAME_MIN_VAL && v <= FLAME_MAX_VAL) {
      violet += 1;
    }
  }
  const skyFrac = sky / n;
  const violetFrac = violet / n;
  const skyValue = sky > 0 ? skyValueSum / sky : 0;
  return {
    shadow:
      skyFrac >= SHADOW_MIN_SKY_FRAC &&
      skyValue <= SHADOW_MAX_SKY_VALUE &&
      violetFrac >= SHADOW_MIN_VIOLET_FRAC &&
      violetFrac <= SHADOW_MAX_VIOLET_FRAC,
    skyFrac: Math.round(skyFrac * 1000) / 1000,
    violetFrac: Math.round(violetFrac * 1000) / 1000,
    skyValue: Math.round(skyValue * 100) / 100,
  };
}
